const MOSCOW_TIME_ZONE = 'Europe/Moscow';

/**
 * Форматирует Date как московское локальное время:
 * YYYY-MM-DDTHH:mm:ss
 *
 * В строке намеренно нет Z и часового смещения,
 * потому что due_date хранится в PostgreSQL как
 * timestamp without time zone.
 */
function formatDateInMoscow(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MOSCOW_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  );

  return [
    `${values.year}-${values.month}-${values.day}`,
    `${values.hour}:${values.minute}:${values.second}`,
  ].join('T');
}

/**
 * Нормализует строку из input[type="datetime-local"].
 *
 * Такая строка уже содержит нужное московское календарное
 * время и не должна проходить через Date → toISOString().
 */
function normalizeLocalDateTimeString(value) {
  const match = String(value).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  if (!match) {
    return null;
  }

  const [
    ,
    year,
    month,
    day,
    hour,
    minute,
    second = '00',
  ] = match;

  const numericValues = [
    Number(year),
    Number(month),
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ];

  if (numericValues.some((item) => !Number.isFinite(item))) {
    return null;
  }

  if (
    Number(month) < 1
    || Number(month) > 12
    || Number(day) < 1
    || Number(day) > 31
    || Number(hour) < 0
    || Number(hour) > 23
    || Number(minute) < 0
    || Number(minute) > 59
    || Number(second) < 0
    || Number(second) > 59
  ) {
    return null;
  }

  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/**
 * Приводит due_date к московскому локальному времени
 * перед PUT/POST /tasks.
 *
 * Результат:
 * YYYY-MM-DDTHH:mm:ss
 *
 * Без Z и без UTC-конвертации.
 */
export function normalizeDueDateForSubmit(submitData) {
  if (!submitData || typeof submitData !== 'object') {
    return;
  }

  if (
    !Object.prototype.hasOwnProperty.call(
      submitData,
      'due_date'
    )
  ) {
    return;
  }

  const value = submitData.due_date;

  if (
    value === null
    || value === ''
    || value === undefined
  ) {
    submitData.due_date = null;
    return;
  }

  if (value instanceof Date) {
    submitData.due_date = formatDateInMoscow(value);
    return;
  }

  if (
    typeof value === 'number'
    && Number.isFinite(value)
  ) {
    submitData.due_date = formatDateInMoscow(
      new Date(value)
    );
    return;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      submitData.due_date = null;
      return;
    }

    /*
     * Строка от datetime-local уже является нужным
     * московским календарным временем. Не трогаем часы.
     */
    const localValue =
      normalizeLocalDateTimeString(trimmed);

    if (localValue) {
      submitData.due_date = localValue;
      return;
    }

    /*
     * Строки с Z или часовым смещением переводим
     * в московское календарное время.
     */
    const parsedDate = new Date(trimmed);

    submitData.due_date = Number.isNaN(
      parsedDate.getTime()
    )
      ? null
      : formatDateInMoscow(parsedDate);
  }
}
